import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import {
  ArrowRight,
  Heart,
  Leaf,
  Users,
  Scale,
  Lightbulb,
  HandHeart,
  Sparkles,
  Calendar,
  Handshake,
  Share2,
  CheckCircle2,
} from 'lucide-react';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useLanguage } from '@/contexts/LanguageContext';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { db } from '@/lib/supabase';

const FORM_URL = 'https://www.artifex-systems.sk/dobrovolnictvo#formular';
const HR_EMAIL = 'hr-loz@artifex-systems.com';

const copy = {
  sk: {
    badge: 'Artifex Social Impact',
    title: 'Spoločenský dopad',
    titleAccent: 'v Artifex',
    lede:
      'Pozitívna zmena pre ľudí, komunity a životné prostredie. Začíname v malom, rastie to s nami — a zapája sa každý z nás.',
    ctaForm: 'Nahlásiť dobrovoľníctvo',
    ctaChampion: 'Stať sa championom',
    whatTitle: 'Čo je social impact?',
    whatP1:
      'Social impact je pozitívna zmena, ktorú vytvárame pre ľudí, komunity a životné prostredie tým, ako pracujeme. Nie je to len o obchodnom úspechu — ide aj o to, ako prispievame k lepšiemu svetu.',
    whatP2: 'Táto cesta začína v malom, rastie postupne a zapája každého z nás.',
    nowBadge: 'Kde sme dnes',
    nowTitle: 'Čo to pre nás znamená práve teraz',
    nowIntro:
      'Sme na začiatku. Zameriavame sa na učenie, objavovanie a budovanie základov. Nemusíme byť dokonalí. Stačí, že sme odhodlaní.',
    pillars: [
      {
        title: 'Podpora komunity',
        text: 'Ako môžeme pomôcť komunite, v ktorej žijeme a pracujeme.',
      },
      {
        title: 'Životné prostredie',
        text: 'Malé kroky, ktoré znižujú odpad a spotrebu energie.',
      },
      {
        title: 'Wellbeing zamestnancov',
        text: 'Pracovisko, kde sa ľudia cítia prijatí a podporovaní.',
      },
      {
        title: 'Etické správanie',
        text: 'Robiť správne veci aj vtedy, keď rastieme.',
      },
      {
        title: 'Zmysluplné inovácie',
        text: 'Premýšľať, ako naša práca rieši skutočné problémy.',
      },
    ],
    youBadge: 'Vaša rola',
    youTitle: 'Čo to znamená pre vás',
    youIntro:
      'Nemusíte byť expert. Nemusíte meniť všetko zo dňa na deň. Vaša úloha je teraz jednoduchá:',
    youItems: [
      'Buďte aktívni',
      'Zdieľajte nápady',
      'Podporte malé zmeny',
      'Zapojte sa, keď príde príležitosť',
      'Pomôžte formovať kultúru',
    ],
    youNote:
      'Social impact nie je niečo, čo sa deje zamestnancom — budujeme ho spolu.',
    communityBadge: 'Komunita',
    communityTitle: 'Pripojte sa na Viva Engage',
    communityIntro:
      'Spúšťame priestor venovaný pozitívnemu dopadu, ktorý môžeme vytvoriť spoločne. Artifex Social Impact má ukázať, ako naši ľudia pomáhajú — dobrovoľníctvom, fundraisingom, podporou komunít a témami, na ktorých im záleží. Veľké aj malé činy sa počítajú.',
    communityItems: [
      'Zdieľajte, do čoho ste zapojení',
      'Predstavte témy, ktoré sú vám blízke',
      'Oceňujte, čo dokázali kolegovia',
      'Inšpirujte ostatných, aby sa zapojili',
    ],
    communityNote:
      'Nemusíte čakať na niečo veľké. Každý kúsok sa počíta a každý príbeh má význam. Vo Viva Engage vyhľadajte komunitu Artifex Social Impact.',
    championBadge: 'Tím',
    championTitle: 'Staňte sa Social Impact championom',
    championIntro:
      'Staňte sa súčasťou tímu a majte slovo pri ďalších komunitných a charitatívnych akciách.',
    championItems: [
      'Buďte v kontakte s vybranou charitou',
      'Pomáhajte pri fundraisingu',
      'Plánujte naše eventy',
      'Vytvárajte skutočný dopad',
      'Rozvíjajte sa a spájajte',
    ],
    championCta: 'Napísať HR tímu',
    championHint: 'Zaregistrujte záujem u miestneho HR tímu.',
    formBadge: 'Už niečo robíte?',
    formTitle: 'Dajte nám vedieť',
    formIntro:
      'Mnohí z vás už robia rozdiel — dobrovoľníčia, podporujú lokálne témy, znižujú odpad, mentorujú, zbierajú peniaze alebo sa zapájajú do komunitných aktivít. Veľké aj malé, pravidelné aj jednorazové. Vaše skúsenosti nám pomôžu nastaviť priority.',
    formName: 'Meno a priezvisko',
    formNamePh: 'Ján Novák',
    formEmail: 'Firemný e-mail',
    formEmailPh: 'jan.novak@artifex-systems.sk',
    formDepartment: 'Oddelenie',
    formDepartmentPh: 'Napr. Výroba, HR, Kvalita',
    formType: 'Čomu sa venujete',
    formOrg: 'Organizácia alebo téma',
    formOrgPh: 'Napr. Červený kríž, zber odpadu, mentoring',
    formDesc: 'Stručne opíšte, čo robíte',
    formDescPh: 'Čo, kde a prečo vám na tom záleží…',
    formFreq: 'Ako často',
    formChampion: 'Mám záujem stať sa Social Impact championom',
    formSubmit: 'Odoslať',
    formSending: 'Odosiela sa…',
    formSuccessTitle: 'Ďakujeme',
    formSuccess:
      'Vaše dobrovoľníctvo sme zaevidovali. Pomáha nám to stavať program, ktorý dáva zmysel.',
    formError: 'Odoslanie sa nepodarilo. Skúste to znova, prosím.',
    qrTitle: 'Naskenujte QR kód',
    qrText:
      'Otvorí sa tento formulár v telefóne. Môžete ho zdieľať aj s kolegami.',
    types: {
      volunteering: 'Dobrovoľníctvo',
      fundraising: 'Fundraising / zbierka',
      mentoring: 'Mentoring',
      environment: 'Životné prostredie / odpad',
      community: 'Komunitná aktivita',
      other: 'Iné',
    },
    freqs: {
      once: 'Skúsil/a som to raz',
      occasionally: 'Občas',
      regularly: 'Pravidelne',
    },
  },
  en: {
    badge: 'Artifex Social Impact',
    title: 'Social impact',
    titleAccent: 'at Artifex',
    lede:
      'Positive change for people, communities and the environment. It starts small, grows with us — and involves every one of us.',
    ctaForm: 'Share your volunteering',
    ctaChampion: 'Become a champion',
    whatTitle: 'What is social impact?',
    whatP1:
      'Social impact is the positive change we create for people, communities and the environment through the way we work. It is about using our business not only to succeed commercially, but also to contribute to a better world.',
    whatP2: 'This journey starts small, grows over time, and involves every one of us.',
    nowBadge: 'Right now',
    nowTitle: 'What this means for us today',
    nowIntro:
      'We are at the beginning. Our focus is on learning, exploring and building the foundations. We do not need to be perfect. We just need to be committed.',
    pillars: [
      {
        title: 'Community support',
        text: 'How we can give back locally, where we live and work.',
      },
      {
        title: 'Environmental responsibility',
        text: 'Small steps to reduce waste and energy use.',
      },
      {
        title: 'Employee wellbeing',
        text: 'A supportive, inclusive workplace where people feel they belong.',
      },
      {
        title: 'Ethical behaviour',
        text: 'Doing the right thing as we grow.',
      },
      {
        title: 'Purpose-driven innovation',
        text: 'Thinking about how our work can solve real problems.',
      },
    ],
    youBadge: 'Your role',
    youTitle: 'What this means for you',
    youIntro:
      'You do not need to be an expert. You do not need to change everything overnight. Your role right now is simple:',
    youItems: [
      'Be active',
      'Share ideas',
      'Support small changes',
      'Participate when opportunities arise',
      'Help shape the culture we want to build',
    ],
    youNote:
      'Social impact is not something done to employees — it is something built with employees.',
    communityBadge: 'Community',
    communityTitle: 'Join us on Viva Engage',
    communityIntro:
      'We are launching a space dedicated to the positive impact we can make together. Artifex Social Impact shines a light on how our people give back — through volunteering, fundraising, supporting local communities and championing causes that matter. Big or small, every action counts.',
    communityItems: [
      'Share what you are involved in',
      'Highlight causes close to your heart',
      'Celebrate the impact of your colleagues',
      'Inspire others to get involved',
    ],
    communityNote:
      'You do not need to wait for something big. Every little bit counts, and every story matters. Search for the Artifex Social Impact community in Viva Engage.',
    championBadge: 'The team',
    championTitle: 'Become a Social Impact champion',
    championIntro:
      'Join the Social Impact team and have your say in our next community and charity events.',
    championItems: [
      'Be in contact with our chosen charity',
      'Help fundraise',
      'Plan our events',
      'Make real impact',
      'Grow and connect',
    ],
    championCta: 'Contact HR',
    championHint: 'Register your interest with your local HR team.',
    formBadge: 'Already making an impact?',
    formTitle: 'Let us know',
    formIntro:
      'As we begin this journey, we want to learn from you. Many of you are already making a difference — volunteering, supporting local causes, reducing waste, mentoring, fundraising or taking part in community initiatives. Regular or one-off, big or small — your contribution matters.',
    formName: 'Full name',
    formNamePh: 'Jane Smith',
    formEmail: 'Work email',
    formEmailPh: 'jane.smith@artifex-systems.sk',
    formDepartment: 'Department',
    formDepartmentPh: 'e.g. Production, HR, Quality',
    formType: 'What you are involved in',
    formOrg: 'Organisation or cause',
    formOrgPh: 'e.g. Red Cross, waste collection, mentoring',
    formDesc: 'Briefly describe what you do',
    formDescPh: 'What, where, and why it matters to you…',
    formFreq: 'How often',
    formChampion: 'I am interested in becoming a Social Impact champion',
    formSubmit: 'Submit',
    formSending: 'Sending…',
    formSuccessTitle: 'Thank you',
    formSuccess:
      'We have recorded your volunteering. It helps us build a programme that is authentic and useful.',
    formError: 'Something went wrong. Please try again.',
    qrTitle: 'Scan the QR code',
    qrText: 'Opens this form on your phone. Feel free to share it with colleagues.',
    types: {
      volunteering: 'Volunteering',
      fundraising: 'Fundraising',
      mentoring: 'Mentoring',
      environment: 'Environment / waste',
      community: 'Community initiative',
      other: 'Other',
    },
    freqs: {
      once: 'I have tried it once',
      occasionally: 'Occasionally',
      regularly: 'Regularly',
    },
  },
} as const;

const pillarIcons = [Users, Leaf, Heart, Scale, Lightbulb];
const activityTypes = ['volunteering', 'fundraising', 'mentoring', 'environment', 'community', 'other'] as const;
const frequencies = ['once', 'occasionally', 'regularly'] as const;

const emptyForm = {
  full_name: '',
  email: '',
  department: '',
  activity_type: '',
  organization: '',
  description: '',
  frequency: '',
  interested_in_champion: false,
};

const fieldClass =
  'h-12 rounded-lg border-2 border-primary/30 bg-muted px-4 text-base text-primary shadow-none placeholder:text-muted-foreground focus-visible:border-secondary focus-visible:ring-2 focus-visible:ring-secondary/40 focus-visible:ring-offset-0';

const textareaClass =
  'min-h-[140px] rounded-lg border-2 border-primary/30 bg-muted px-4 py-3 text-base text-primary shadow-none placeholder:text-muted-foreground focus-visible:border-secondary focus-visible:ring-2 focus-visible:ring-secondary/40 focus-visible:ring-offset-0';

const selectClass =
  'h-12 rounded-lg border-2 border-primary/30 bg-muted px-4 text-base text-primary shadow-none focus:ring-2 focus:ring-secondary/40 focus:ring-offset-0';

const Dobrovolnictvo = () => {
  const { language } = useLanguage();
  const t = copy[language];
  const { toast } = useToast();
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title =
      language === 'sk'
        ? 'Dobrovoľníctvo a social impact | Artifex Systems Slovakia'
        : 'Volunteering & social impact | Artifex Systems Slovakia';

    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex, nofollow';
    document.head.appendChild(robots);

    if (window.location.hash === '#formular') {
      document.getElementById('formular')?.scrollIntoView({ behavior: 'smooth' });
    }

    return () => {
      robots.remove();
    };
  }, [language]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.activity_type || !form.description.trim()) {
      toast({
        title: language === 'sk' ? 'Doplňte povinné polia' : 'Please fill in required fields',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    try {
      await db.volunteering.submit({
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        department: form.department.trim() || null,
        activity_type: form.activity_type,
        organization: form.organization.trim() || null,
        description: form.description.trim(),
        frequency: form.frequency || null,
        interested_in_champion: form.interested_in_champion,
      });
      setSubmitted(true);
      setForm(emptyForm);
      toast({ title: t.formSuccessTitle, description: t.formSuccess });
    } catch {
      toast({ title: t.formError, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-md border-b border-[hsl(var(--hairline))]">
        <div className="container-tight h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" aria-label="Artifex — home">
            <img src="/artifex-logo.svg" alt="Artifex Systems Slovakia" className="h-7 w-auto" />
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      <section className="relative overflow-hidden surface-dark grain">
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(hsl(0 0% 100%) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100%) 1px, transparent 1px)',
            backgroundSize: '96px 96px',
          }}
          aria-hidden
        />
        <div className="container-tight relative z-10 py-20 md:py-28">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="eyebrow text-secondary mb-6 block">{t.badge}</span>
            <h1 className="font-display font-bold text-white max-w-4xl" style={{ fontSize: 'clamp(2.6rem, 7vw, 5.5rem)', lineHeight: 0.98, letterSpacing: '-0.035em' }}>
              {t.title} <em className="not-italic text-secondary">{t.titleAccent}</em>
            </h1>
            <p className="mt-8 max-w-2xl text-lg md:text-xl text-white/75 leading-relaxed">{t.lede}</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a href="#formular" className="btn-accent">
                {t.ctaForm}
                <ArrowRight className="w-4 h-4" />
              </a>
              <a href="#champion" className="btn-ghost-light">
                {t.ctaChampion}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-tight grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <span className="eyebrow mb-6 block">01</span>
            <h2 className="text-primary">{t.whatTitle}</h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 space-y-5">
            <p className="lede">{t.whatP1}</p>
            <p className="text-foreground/75 leading-relaxed">{t.whatP2}</p>
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="container-tight">
          <div className="mb-12 max-w-3xl">
            <span className="eyebrow mb-4 block">{t.nowBadge}</span>
            <h2 className="text-primary mb-5">{t.nowTitle}</h2>
            <p className="lede">{t.nowIntro}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-[hsl(var(--hairline))] border border-[hsl(var(--hairline))] rounded-xl overflow-hidden">
            {t.pillars.map((pillar, i) => {
              const Icon = pillarIcons[i];
              return (
                <div key={pillar.title} className="bg-background p-7 hover:bg-muted/40 transition-colors">
                  <div className="flex items-center justify-between mb-6">
                    <span className="section-num">{String(i + 1).padStart(2, '0')}</span>
                    <Icon className="w-5 h-5 text-secondary" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-primary mb-3">{pillar.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{pillar.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-pad bg-muted/50">
        <div className="container-tight grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="eyebrow mb-6 block">{t.youBadge}</span>
            <h2 className="text-primary mb-5">{t.youTitle}</h2>
            <p className="lede">{t.youIntro}</p>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <ul className="space-y-0">
              {t.youItems.map((item, i) => (
                <li
                  key={item}
                  className="flex items-center gap-4 py-5 border-b border-[hsl(var(--hairline))] font-display text-lg text-primary"
                >
                  <span className="text-xs tabular-nums tracking-[0.18em] text-muted-foreground w-8">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-8 text-foreground/80 italic">{t.youNote}</p>
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-tight grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <span className="eyebrow mb-6 block">{t.communityBadge}</span>
            <h2 className="text-primary mb-5">{t.communityTitle}</h2>
            <p className="text-foreground/75 leading-relaxed mb-8">{t.communityIntro}</p>
            <ul className="space-y-4 mb-8">
              {t.communityItems.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <Share2 className="w-4 h-4 text-secondary mt-1 shrink-0" />
                  <span className="text-primary font-display">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-muted-foreground leading-relaxed">{t.communityNote}</p>
          </div>

          <div id="champion" className="scroll-mt-24 rounded-xl border border-[hsl(var(--hairline))] p-8 md:p-10 bg-card shadow-[var(--shadow-card)]">
            <span className="eyebrow mb-6 block">{t.championBadge}</span>
            <h2 className="text-primary mb-4" style={{ fontSize: 'clamp(1.6rem, 2.4vw, 2.1rem)' }}>
              {t.championTitle}
            </h2>
            <p className="text-muted-foreground mb-8">{t.championIntro}</p>
            <ul className="space-y-3 mb-8">
              {t.championItems.map((item) => (
                <li key={item} className="flex items-center gap-3 text-primary">
                  <Sparkles className="w-4 h-4 text-secondary shrink-0" />
                  <span className="font-display">{item}</span>
                </li>
              ))}
            </ul>
            <a
              href={`mailto:${HR_EMAIL}?subject=${encodeURIComponent('Social Impact champion')}`}
              className="btn-primary"
            >
              {t.championCta}
              <ArrowRight className="w-4 h-4" />
            </a>
            <p className="mt-4 text-xs text-muted-foreground">{t.championHint}</p>
          </div>
        </div>
      </section>

      <section id="formular" className="section-pad bg-muted/40 scroll-mt-20">
        <div className="container-tight">
          <div className="max-w-3xl mb-12">
            <span className="eyebrow mb-4 block">{t.formBadge}</span>
            <h2 className="text-primary mb-5">{t.formTitle}</h2>
            <p className="lede">{t.formIntro}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-28 rounded-xl border border-[hsl(var(--hairline))] bg-background p-8 text-center shadow-[var(--shadow-card)]">
                <HandHeart className="w-6 h-6 text-secondary mx-auto mb-4" />
                <h3 className="font-display text-xl font-semibold text-primary mb-2">{t.qrTitle}</h3>
                <p className="text-sm text-muted-foreground mb-6">{t.qrText}</p>
                <div className="inline-flex p-4 bg-white rounded-lg border border-[hsl(var(--hairline))]">
                  <QRCodeSVG
                    value={FORM_URL}
                    size={196}
                    level="M"
                    bgColor="#ffffff"
                    fgColor="#21394F"
                    title={FORM_URL}
                  />
                </div>
                <p className="mt-4 text-xs font-mono text-muted-foreground break-all">
                  artifex-systems.sk/dobrovolnictvo
                </p>
              </div>
            </div>

            <div className="lg:col-span-8">
              {submitted ? (
                <div className="rounded-xl border border-[hsl(var(--hairline))] bg-background p-10 md:p-14 text-center shadow-[var(--shadow-card)]">
                  <CheckCircle2 className="w-12 h-12 text-secondary mx-auto mb-5" />
                  <h3 className="font-display text-2xl font-semibold text-primary mb-3">{t.formSuccessTitle}</h3>
                  <p className="lede mx-auto">{t.formSuccess}</p>
                  <button type="button" className="btn-secondary mt-8" onClick={() => setSubmitted(false)}>
                    {language === 'sk' ? 'Odoslať ďalšie' : 'Submit another'}
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="rounded-xl border-2 border-primary/15 bg-white p-8 md:p-10 shadow-[var(--shadow-lift)] space-y-6"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="full_name" className="font-display text-sm font-semibold text-primary">
                        {t.formName} *
                      </Label>
                      <Input
                        id="full_name"
                        required
                        placeholder={t.formNamePh}
                        className={fieldClass}
                        value={form.full_name}
                        onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="font-display text-sm font-semibold text-primary">
                        {t.formEmail} *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        placeholder={t.formEmailPh}
                        className={fieldClass}
                        value={form.email}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="department" className="font-display text-sm font-semibold text-primary">
                      {t.formDepartment}
                    </Label>
                    <Input
                      id="department"
                      placeholder={t.formDepartmentPh}
                      className={fieldClass}
                      value={form.department}
                      onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label className="font-display text-sm font-semibold text-primary">{t.formType} *</Label>
                      <Select
                        value={form.activity_type || undefined}
                        onValueChange={(value) => setForm((f) => ({ ...f, activity_type: value }))}
                      >
                        <SelectTrigger className={selectClass}>
                          <SelectValue placeholder={t.formType} />
                        </SelectTrigger>
                        <SelectContent>
                          {activityTypes.map((type) => (
                            <SelectItem key={type} value={type}>
                              {t.types[type]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="font-display text-sm font-semibold text-primary">{t.formFreq}</Label>
                      <Select
                        value={form.frequency || undefined}
                        onValueChange={(value) => setForm((f) => ({ ...f, frequency: value }))}
                      >
                        <SelectTrigger className={selectClass}>
                          <SelectValue placeholder={t.formFreq} />
                        </SelectTrigger>
                        <SelectContent>
                          {frequencies.map((freq) => (
                            <SelectItem key={freq} value={freq}>
                              {t.freqs[freq]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="organization" className="font-display text-sm font-semibold text-primary">
                      {t.formOrg}
                    </Label>
                    <Input
                      id="organization"
                      placeholder={t.formOrgPh}
                      className={fieldClass}
                      value={form.organization}
                      onChange={(e) => setForm((f) => ({ ...f, organization: e.target.value }))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description" className="font-display text-sm font-semibold text-primary">
                      {t.formDesc} *
                    </Label>
                    <Textarea
                      id="description"
                      required
                      rows={5}
                      placeholder={t.formDescPh}
                      className={textareaClass}
                      value={form.description}
                      onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    />
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer rounded-lg border-2 border-primary/20 bg-muted/60 p-4">
                    <Checkbox
                      checked={form.interested_in_champion}
                      onCheckedChange={(checked) =>
                        setForm((f) => ({ ...f, interested_in_champion: checked === true }))
                      }
                      className="mt-0.5 h-5 w-5 border-2 border-primary"
                    />
                    <span className="text-sm text-primary leading-relaxed">{t.formChampion}</span>
                  </label>

                  <button type="submit" className="btn-primary" disabled={submitting}>
                    {submitting ? t.formSending : t.formSubmit}
                    {!submitting && <Handshake className="w-4 h-4" />}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[hsl(var(--hairline))] py-8">
        <div className="container-tight flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
          <span className="font-display">Artifex Systems Slovakia · Social Impact</span>
          <span className="inline-flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5" />
            {language === 'sk' ? 'Interná stránka — nie je v navigácii webu' : 'Internal page — not listed in site navigation'}
          </span>
        </div>
      </footer>
    </div>
  );
};

export default Dobrovolnictvo;
